import { create } from 'zustand';
import { Language, LocalizationConfig, TranslationKey } from '../types/localization';
import { en } from '../locales/en';
import { ar } from '../locales/ar';

interface LocalizationStore {
  config: LocalizationConfig;
  translations: Record<Language, Record<TranslationKey, string>>;

  // Actions
  setLanguage: (language: Language) => void;
  // `params` may be an interpolation map, or a plain default/fallback string
  // some call sites pass as a second argument (ignored when the key resolves).
  t: (key: TranslationKey, params?: Record<string, string | number> | string) => string;
  getDirection: () => 'ltr' | 'rtl';
  getCurrentLanguage: () => Language;

  // Persist to localStorage
  loadFromStorage: () => void;
  saveToStorage: () => void;
}

export const useLocalizationStore = create<LocalizationStore>((set, get) => ({
  config: {
    language: 'en',
    direction: 'ltr',
  },
  translations: {
    en,
    ar,
  },
  
  setLanguage: (language) => {
    const languageDirection = language === 'ar' ? 'rtl' : 'ltr';
    // Per-user accessibility override (set via /accessibility-prefs):
    // a doctor who reads English content RTL (or vice versa) keeps
    // their layout direction across language switches. The override is
    // mirrored onto <html>'s data attribute by useAccessibilityStore;
    // we read from there to avoid a circular store import.
    const overrideAttr =
      typeof document !== 'undefined'
        ? document.documentElement.dataset.directionOverride
        : undefined;
    const direction =
      overrideAttr === 'rtl' || overrideAttr === 'ltr'
        ? overrideAttr
        : languageDirection;

    set({
      config: {
        language,
        direction,
      },
    });

    // Update document direction
    document.documentElement.dir = direction;
    document.documentElement.lang = language;

    // Save to storage
    get().saveToStorage();
  },
  
  t: (key, params) => {
    const { config, translations } = get();
    let translation = translations[config.language][key] || translations['en'][key] || key;

    // Replace parameters if provided (string params are treated as a plain
    // default/fallback and require no interpolation).
    if (params && typeof params === 'object') {
      Object.keys(params).forEach((paramKey) => {
        translation = translation.replace(
          new RegExp(`{{${paramKey}}}`, 'g'),
          String(params[paramKey])
        );
      });
    }

    return translation;
  },
  
  getDirection: () => get().config.direction,
  
  getCurrentLanguage: () => get().config.language,
  
  loadFromStorage: () => {
    try {
      const stored = localStorage.getItem('hospital-localization');
      if (stored) {
        const config: LocalizationConfig = JSON.parse(stored);
        set({ config });
        
        // Update document direction
        document.documentElement.dir = config.direction;
        document.documentElement.lang = config.language;
      }
    } catch (error) {
      console.error('Failed to load localization config:', error);
    }
  },
  
  saveToStorage: () => {
    try {
      const { config } = get();
      localStorage.setItem('hospital-localization', JSON.stringify(config));
    } catch (error) {
      console.error('Failed to save localization config:', error);
    }
  },
}));